import { useMemo, useState } from "react";
import { PageContainer } from "@swot/base-components";
import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
  type CmtsGridRowAction,
} from "@components/data-grid/index.js";
import { ApiErrorPanel } from "@components/errors/index.js";
import {
  CmtsAuditFilterPanel,
  type CmtsAuditFilters,
} from "./CmtsAuditFilterPanel.js";
import type { AuditExportFormat } from "@modules/pages/cmtsAuditLogsPage.js";

type Row = Record<string, unknown>;

type AuditGridProps = {
  readonly pageTitle?: string;
  readonly rows: readonly Row[];
  readonly exportHistory?: readonly Row[];
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly onOpenEvent?: (row: Row) => void | Promise<Row | void>;
  readonly onExportAudit?: (
    rows: readonly Row[],
    format: AuditExportFormat,
  ) => void | Promise<void>;
  readonly onOpenLinkedRecord?: (row: Row) => void | Promise<void>;
};

type DetailItem = {
  readonly label: string;
  readonly value: string;
};

const eventColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "occurredAt",
    header: "Occurred At",
    accessor: readOccurredAt,
    sortable: true,
    filterable: true,
  },
  {
    id: "action",
    header: "Action",
    accessor: readAction,
    sortable: true,
    filterable: true,
  },
  {
    id: "entityType",
    header: "Entity",
    accessor: readEntity,
    sortable: true,
    filterable: true,
  },
  {
    id: "actor",
    header: "Actor",
    accessor: readActor,
    sortable: true,
    filterable: true,
  },
  {
    id: "result",
    header: "Result",
    accessor: readResult,
    sortable: true,
    filterable: true,
  },
  {
    id: "linkedRecord",
    header: "Linked Record",
    accessor: readLinkedRecord,
    sortable: true,
    filterable: true,
  },
  {
    id: "correlation",
    header: "Correlation",
    accessor: readCorrelationStatus,
    sortable: true,
    filterable: true,
  },
];

const exportHistoryColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "exportedAt",
    header: "Exported At",
    accessor: readExportedAt,
    sortable: true,
    filterable: true,
  },
  {
    id: "format",
    header: "Format",
    accessor: readExportFormat,
    sortable: true,
    filterable: true,
  },
  {
    id: "status",
    header: "Status",
    accessor: readExportStatus,
    sortable: true,
    filterable: true,
  },
  {
    id: "rowCount",
    header: "Records",
    accessor: readExportRowCount,
    sortable: true,
    filterable: true,
  },
  {
    id: "requestedBy",
    header: "Requested By",
    accessor: readExportActor,
    sortable: true,
    filterable: true,
  },
  {
    id: "fileName",
    header: "File",
    accessor: readExportFile,
    sortable: true,
    filterable: true,
  },
];

export function CmtsAuditLogGrid(props: AuditGridProps) {
  const [filters, setFilters] = useState<CmtsAuditFilters>({});
  const [detailRow, setDetailRow] = useState<Row | null>(null);
  const [actionRow, setActionRow] = useState<Row | null>(null);
  const [correlationSource, setCorrelationSource] = useState<Row | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] =
    useState<AuditExportFormat>("CSV");
  const [exporting, setExporting] = useState(false);

  const normalizedRows = useMemo(
    () => ensureGridIds(props.rows, "audit-event"),
    [props.rows],
  );

  const normalizedExportHistory = useMemo(
    () =>
      ensureGridIds(props.exportHistory ?? [], "audit-export"),
    [props.exportHistory],
  );

  const visibleRows = useMemo(
    () => applyAuditFilters(normalizedRows, filters),
    [normalizedRows, filters],
  );

  const correlationRows = useMemo(() => {
    const correlationId = readRawCorrelation(correlationSource);

    if (!correlationId) {
      return [];
    }

    return normalizedRows.filter(
      (row) => readRawCorrelation(row) === correlationId,
    );
  }, [normalizedRows, correlationSource]);

  const actions: readonly CmtsGridRowAction<Row>[] = [
    {
      id: "audit-actions",
      label: "⋮ Actions",
      onClick: (row) => {
        setActionRow(row);
      },
    },
  ];

  async function openDetail(row: Row) {
    setActionRow(null);

    const response = await props.onOpenEvent?.(row);
    setDetailRow(response ?? row);
  }

  async function openLinkedRecord(row: Row) {
    setActionRow(null);
    await props.onOpenLinkedRecord?.(row);
  }

  function openCorrelation(row: Row) {
    setActionRow(null);
    setCorrelationSource(row);
  }

  async function handleExport() {
    if (!props.onExportAudit || visibleRows.length === 0) {
      return;
    }

    setExporting(true);

    try {
      await props.onExportAudit(visibleRows, exportFormat);
      downloadDisplayedRows(visibleRows, exportFormat);
      setExportOpen(false);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="cmts-audit-blue">
      <PageContainer
        title={props.pageTitle ?? "Audit Logs"}
        subtitle="Review governed audit activity, linked records and correlation history."
      >
        <section className="cmts-audit-hero">
          <div>
            <span className="cmts-audit-hero__eyebrow">
              AUDIT &amp; SECURITY
            </span>

            <h2>Audit activity center</h2>

            <p>
              Review system activity, user actions, linked complaints and
              governed export records.
            </p>
          </div>

          <div className="cmts-audit-hero__summary">
            <span>Visible events</span>
            <strong>{visibleRows.length}</strong>
          </div>
        </section>

        <section className="cmts-audit-toolbar">
          <div className="cmts-audit-toolbar__heading">
            <div>
              <h3>Audit events</h3>
              <p>
                Search and filter backend audit records.
              </p>
            </div>
          </div>

          <div className="cmts-audit-toolbar__controls">
            <div className="cmts-audit-filter-wrapper">
              <CmtsAuditFilterPanel onChange={setFilters} />
            </div>

            <button
              type="button"
              className="cmts-audit-export-button"
              disabled={
                !props.onExportAudit ||
                Boolean(props.loading) ||
                visibleRows.length === 0
              }
              onClick={() => setExportOpen(true)}
            >
              <span aria-hidden="true">⇩</span>
              Export Audit
            </button>
          </div>
        </section>

        {props.error ? (
          <ApiErrorPanel error={new Error(props.error)} />
        ) : null}

        <section className="cmts-audit-grid-card">
          <CmtsSmartDataGrid<Row>
            title={`${visibleRows.length} audit ${
              visibleRows.length === 1 ? "event" : "events"
            }`}
            rows={visibleRows}
            columns={eventColumns}
            rowKey="id"
            defaultPageSize={10}
            sortable
            filterable
            loading={props.loading}
            error={props.error}
            actions={actions}
            onRowClick={openDetail}
            emptyMessage="No audit events are available for the selected filters."
          />
        </section>

        <section className="cmts-audit-history-section">
          <div className="cmts-audit-section-heading">
            <div>
              <span className="cmts-audit-section-icon">⇩</span>

              <div>
                <h3>Audit export history</h3>
                <p>
                  Governed audit export requests created for this tenant.
                </p>
              </div>
            </div>

            <span className="cmts-audit-count-badge">
              {normalizedExportHistory.length} exports
            </span>
          </div>

          <div className="cmts-audit-grid-card cmts-audit-grid-card--history">
            <CmtsSmartDataGrid<Row>
              title="Governed audit exports"
              rows={normalizedExportHistory}
              columns={exportHistoryColumns}
              rowKey="id"
              defaultPageSize={10}
              searchable
              sortable
              filterable
              emptyMessage="No audit export requests have been created."
            />
          </div>
        </section>
      </PageContainer>

      {actionRow ? (
        <AuditActionModal
          row={actionRow}
          canOpenLinkedRecord={
            Boolean(props.onOpenLinkedRecord) &&
            hasLinkedComplaint(actionRow)
          }
          canOpenCorrelation={Boolean(
            readRawCorrelation(actionRow),
          )}
          onClose={() => setActionRow(null)}
          onViewDetail={() => void openDetail(actionRow)}
          onOpenLinkedRecord={() =>
            void openLinkedRecord(actionRow)
          }
          onOpenCorrelation={() =>
            openCorrelation(actionRow)
          }
        />
      ) : null}

      {detailRow ? (
        <AuditDetailModal
          row={detailRow}
          canOpenLinkedRecord={
            Boolean(props.onOpenLinkedRecord) &&
            hasLinkedComplaint(detailRow)
          }
          onClose={() => setDetailRow(null)}
          onOpenLinkedRecord={() =>
            void openLinkedRecord(detailRow)
          }
        />
      ) : null}

      {correlationSource ? (
        <CorrelationModal
          rows={correlationRows}
          onClose={() => setCorrelationSource(null)}
          onOpenDetail={(row) => void openDetail(row)}
        />
      ) : null}

      {exportOpen ? (
        <AuditExportModal
          recordCount={visibleRows.length}
          selectedFormat={exportFormat}
          exporting={exporting}
          onFormatChange={setExportFormat}
          onClose={() => {
            if (!exporting) {
              setExportOpen(false);
            }
          }}
          onExport={() => void handleExport()}
        />
      ) : null}
    </div>
  );
}

function AuditActionModal(props: {
  readonly row: Row;
  readonly canOpenLinkedRecord: boolean;
  readonly canOpenCorrelation: boolean;
  readonly onClose: () => void;
  readonly onViewDetail: () => void;
  readonly onOpenLinkedRecord: () => void;
  readonly onOpenCorrelation: () => void;
}) {
  return (
    <ModalShell
      title="Audit actions"
      subtitle={readAction(props.row)}
      onClose={props.onClose}
    >
      <div className="cmts-audit-action-list">
        <button
          type="button"
          className="cmts-audit-action-option"
          onClick={props.onViewDetail}
        >
          <span className="cmts-audit-action-option__icon">◉</span>

          <span>
            <strong>View details</strong>
            <small>
              Review the complete readable audit information.
            </small>
          </span>

          <span className="cmts-audit-action-option__arrow">›</span>
        </button>

        <button
          type="button"
          className="cmts-audit-action-option"
          disabled={!props.canOpenLinkedRecord}
          onClick={props.onOpenLinkedRecord}
        >
          <span className="cmts-audit-action-option__icon">↗</span>

          <span>
            <strong>Open linked record</strong>
            <small>
              {props.canOpenLinkedRecord
                ? `Open ${readLinkedRecord(props.row)}`
                : "No complaint record is linked"}
            </small>
          </span>

          <span className="cmts-audit-action-option__arrow">›</span>
        </button>

        <button
          type="button"
          className="cmts-audit-action-option"
          disabled={!props.canOpenCorrelation}
          onClick={props.onOpenCorrelation}
        >
          <span className="cmts-audit-action-option__icon">⛓</span>

          <span>
            <strong>View correlation chain</strong>
            <small>
              {props.canOpenCorrelation
                ? "Review related audit events"
                : "No correlation chain is available"}
            </small>
          </span>

          <span className="cmts-audit-action-option__arrow">›</span>
        </button>
      </div>
    </ModalShell>
  );
}

function AuditDetailModal(props: {
  readonly row: Row;
  readonly canOpenLinkedRecord: boolean;
  readonly onClose: () => void;
  readonly onOpenLinkedRecord: () => void;
}) {
  const details = buildDetailItems(props.row);

  return (
    <ModalShell
      title="Audit event details"
      subtitle={readAction(props.row)}
      onClose={props.onClose}
      wide
    >
      <div className="cmts-audit-detail-banner">
        <div>
          <span>Recorded action</span>
          <strong>{readAction(props.row)}</strong>
        </div>

        <span className="cmts-audit-result-pill">
          {readResult(props.row)}
        </span>
      </div>

      <div className="cmts-audit-detail-grid">
        {details.map((item) => (
          <div
            key={item.label}
            className="cmts-audit-detail-item"
          >
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-secondary-button"
          onClick={props.onClose}
        >
          Close
        </button>

        {props.canOpenLinkedRecord ? (
          <button
            type="button"
            className="cmts-audit-primary-button"
            onClick={props.onOpenLinkedRecord}
          >
            Open linked record
          </button>
        ) : null}
      </div>
    </ModalShell>
  );
}

function CorrelationModal(props: {
  readonly rows: readonly Row[];
  readonly onClose: () => void;
  readonly onOpenDetail: (row: Row) => void;
}) {
  return (
    <ModalShell
      title="Correlation chain"
      subtitle={`${props.rows.length} related ${
        props.rows.length === 1 ? "event" : "events"
      }`}
      onClose={props.onClose}
      wide
    >
      {props.rows.length ? (
        <div className="cmts-correlation-list">
          {props.rows.map((row, index) => (
            <button
              type="button"
              key={String(row.id ?? index)}
              className="cmts-correlation-item"
              onClick={() => props.onOpenDetail(row)}
            >
              <span className="cmts-correlation-item__number">
                {index + 1}
              </span>

              <span className="cmts-correlation-item__content">
                <strong>{readAction(row)}</strong>
                <small>
                  {readEntity(row)} • {readActor(row)}
                </small>
              </span>

              <span className="cmts-correlation-item__date">
                {readOccurredAt(row)}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="cmts-audit-empty-state">
          No related audit events were found.
        </div>
      )}

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Done
        </button>
      </div>
    </ModalShell>
  );
}

function AuditExportModal(props: {
  readonly recordCount: number;
  readonly selectedFormat: AuditExportFormat;
  readonly exporting: boolean;
  readonly onFormatChange: (format: AuditExportFormat) => void;
  readonly onClose: () => void;
  readonly onExport: () => void;
}) {
  const formats: readonly {
    format: AuditExportFormat;
    title: string;
    description: string;
    badge: string;
  }[] = [
    {
      format: "CSV",
      title: "CSV spreadsheet",
      description: "Best for Excel, data imports and table analysis.",
      badge: "CSV",
    },
    {
      format: "XLS",
      title: "Excel workbook",
      description: "Formatted spreadsheet for business reporting.",
      badge: "XLS",
    },
    {
      format: "JSON",
      title: "JSON data",
      description: "Structured data for development and integrations.",
      badge: "JSON",
    },
  ];

  return (
    <ModalShell
      title="Export records"
      subtitle="Download the currently displayed audit records."
      onClose={props.onClose}
      wide
    >
      <div className="cmts-export-banner">
        <span className="cmts-export-banner__icon">⇩</span>

        <div>
          <small>DATA EXPORT</small>
          <strong>Prepare your export</strong>
          <p>
            Select a format and download the filtered audit records
            using the displayed values.
          </p>
        </div>

        <span className="cmts-export-record-count">
          {props.recordCount} records
        </span>
      </div>

      <section className="cmts-export-format-section">
        <div className="cmts-export-format-heading">
          <span>01</span>

          <div>
            <strong>Select file format</strong>
            <small>
              Choose the format required for this download.
            </small>
          </div>

          <em>Required</em>
        </div>

        <div className="cmts-export-format-grid">
          {formats.map((item) => {
            const selected =
              props.selectedFormat === item.format;

            return (
              <button
                type="button"
                key={item.format}
                className={`cmts-export-format-card${
                  selected
                    ? " cmts-export-format-card--selected"
                    : ""
                }`}
                onClick={() =>
                  props.onFormatChange(item.format)
                }
              >
                <span className="cmts-export-format-badge">
                  {item.badge}
                </span>

                <span
                  className={`cmts-export-radio${
                    selected
                      ? " cmts-export-radio--selected"
                      : ""
                  }`}
                >
                  {selected ? "✓" : ""}
                </span>

                <strong>{item.title}</strong>
                <small>{item.description}</small>
              </button>
            );
          })}
        </div>
      </section>

      <div className="cmts-export-info">
        <span>i</span>

        <p>
          <strong>Current view will be exported</strong>
          Active search, filters and displayed readable values will
          be applied to the downloaded file.
        </p>
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-secondary-button"
          disabled={props.exporting}
          onClick={props.onClose}
        >
          Cancel
        </button>

        <button
          type="button"
          className="cmts-audit-primary-button"
          disabled={props.exporting}
          onClick={props.onExport}
        >
          {props.exporting
            ? "Preparing..."
            : `Download ${props.selectedFormat}`}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell(props: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: React.ReactNode;
  readonly onClose: () => void;
  readonly wide?: boolean;
}) {
  return (
    <div
      className="cmts-audit-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          props.onClose();
        }
      }}
    >
      <section
        className={`cmts-audit-modal${
          props.wide ? " cmts-audit-modal--wide" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
      >
        <header className="cmts-audit-modal__header">
          <div>
            <h3>{props.title}</h3>
            {props.subtitle ? <p>{props.subtitle}</p> : null}
          </div>

          <button
            type="button"
            className="cmts-audit-modal__close"
            aria-label="Close"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>

        <div className="cmts-audit-modal__body">
          {props.children}
        </div>
      </section>
    </div>
  );
}

function applyAuditFilters(
  rows: readonly Row[],
  filters: CmtsAuditFilters,
): readonly Row[] {
  const values = filters as Record<string, unknown>;
  const search = normalizeFilter(values.search);

  return rows.filter((row) => {
    const searchableText = [
      readOccurredAt(row),
      readAction(row),
      readEntity(row),
      readActor(row),
      readResult(row),
      readLinkedRecord(row),
      readText(row, ["summary", "description", "message"]),
    ]
      .join(" ")
      .toLowerCase();

    if (search && !searchableText.includes(search)) {
      return false;
    }

    if (
      !matchesSelectedFilter(
        readAction(row),
        values.action ?? values.actionCode,
      )
    ) {
      return false;
    }

    if (
      !matchesSelectedFilter(
        readEntity(row),
        values.entity ?? values.entityType,
      )
    ) {
      return false;
    }

    if (
      !matchesSelectedFilter(
        readActor(row),
        values.actor ?? values.actorId,
      )
    ) {
      return false;
    }

    if (
      !matchesSelectedFilter(readResult(row), values.result)
    ) {
      return false;
    }

    if (
      !matchesSelectedFilter(
        readCorrelationStatus(row),
        values.correlation,
      )
    ) {
      return false;
    }

    return true;
  });
}

function matchesSelectedFilter(
  rowValue: string,
  selectedValue: unknown,
): boolean {
  const selected = normalizeFilter(selectedValue);

  if (
    !selected ||
    selected === "all" ||
    selected.startsWith("all ")
  ) {
    return true;
  }

  return rowValue.toLowerCase().includes(selected);
}

function normalizeFilter(value: unknown): string {
  return String(value ?? "").trim().toLowerCase();
}

function ensureGridIds(
  rows: readonly Row[],
  prefix: string,
): readonly Row[] {
  return rows.map((row, index) => ({
    ...row,
    id:
      row.id ??
      row.auditId ??
      row.eventId ??
      row.exportId ??
      row.requestId ??
      `${prefix}-${index + 1}`,
  }));
}

function readOccurredAt(row: Row): string {
  const value = firstValue(row, [
    "occurredAt",
    "createdAt",
    "eventAt",
    "timestamp",
    "auditDate",
    "recordedAt",
  ]);

  return formatDateTime(value);
}

function readAction(row: Row): string {
  return humanize(
    firstValue(row, [
      "actionName",
      "action",
      "actionCode",
      "eventType",
      "eventName",
      "operation",
    ]),
    "Audit Event",
  );
}

function readEntity(row: Row): string {
  return humanize(
    firstValue(row, [
      "entityName",
      "entityDisplayName",
      "entityType",
      "resourceType",
      "moduleName",
      "module",
      "sourceModule",
    ]),
    "System",
  );
}

function readActor(row: Row): string {
  const value = firstValue(row, [
    "actorName",
    "actorDisplayName",
    "actorUserName",
    "userName",
    "performedByName",
    "requestedByName",
    "actorEmail",
    "actorId",
  ]);

  const text = String(value ?? "").trim();

  if (!text || text.toLowerCase() === "system") {
    return "System";
  }

  if (isUuid(text)) {
    return "System user";
  }

  return humanize(text, "System");
}

function readResult(row: Row): string {
  const directValue = firstValue(row, [
    "resultName",
    "result",
    "outcome",
    "statusName",
    "status",
  ]);

  if (directValue !== undefined && directValue !== null) {
    const value = humanize(directValue, "Recorded");
    const normalized = value.toLowerCase();

    if (
      normalized.includes("success") ||
      normalized.includes("complete") ||
      normalized.includes("approved") ||
      normalized.includes("recorded")
    ) {
      return `🟢 ${value}`;
    }

    if (
      normalized.includes("fail") ||
      normalized.includes("reject") ||
      normalized.includes("error") ||
      normalized.includes("denied")
    ) {
      return `🔴 ${value}`;
    }

    if (
      normalized.includes("pending") ||
      normalized.includes("progress") ||
      normalized.includes("queued")
    ) {
      return `🟠 ${value}`;
    }

    return `🔵 ${value}`;
  }

  if (typeof row.success === "boolean") {
    return row.success ? "🟢 Success" : "🔴 Failed";
  }

  return "🔵 Recorded";
}

function readLinkedRecord(row: Row): string {
  const code = firstValue(row, [
    "complaintNumber",
    "complaintCode",
    "complaintRegistrationNumber",
    "recordNumber",
    "entityCode",
    "linkedRecordCode",
  ]);

  const codeText = String(code ?? "").trim();

  if (codeText && !isUuid(codeText)) {
    return codeText;
  }

  const entityType = readEntity(row).toLowerCase();
  const linkedId = firstValue(row, [
    "complaintId",
    "entityId",
    "recordId",
  ]);

  if (linkedId && entityType.includes("complaint")) {
    return "Complaint linked";
  }

  return "No linked record";
}

function readCorrelationStatus(row: Row): string {
  return readRawCorrelation(row)
    ? "🔗 Chain available"
    : "No correlation";
}

function readRawCorrelation(row: Row | null): string {
  if (!row) {
    return "";
  }

  return String(
    firstValue(row, [
      "correlationId",
      "requestId",
      "traceId",
      "operationId",
    ]) ?? "",
  ).trim();
}

function readExportedAt(row: Row): string {
  return formatDateTime(
    firstValue(row, [
      "exportedAt",
      "completedAt",
      "createdAt",
      "requestedAt",
      "occurredAt",
    ]),
  );
}

function readExportFormat(row: Row): string {
  return String(
    firstValue(row, [
      "exportFormat",
      "format",
      "fileFormat",
      "type",
    ]) ?? "CSV",
  ).toUpperCase();
}

function readExportStatus(row: Row): string {
  const value = humanize(
    firstValue(row, [
      "statusName",
      "status",
      "exportStatus",
      "state",
    ]),
    "Completed",
  );

  const normalized = value.toLowerCase();

  if (
    normalized.includes("complete") ||
    normalized.includes("success")
  ) {
    return `🟢 ${value}`;
  }

  if (
    normalized.includes("fail") ||
    normalized.includes("error")
  ) {
    return `🔴 ${value}`;
  }

  if (
    normalized.includes("pending") ||
    normalized.includes("process")
  ) {
    return `🟠 ${value}`;
  }

  return `🔵 ${value}`;
}

function readExportRowCount(row: Row): string {
  const value = firstValue(row, [
    "rowCount",
    "recordCount",
    "totalRows",
    "exportedRows",
  ]);

  const numericValue = Number(value);

  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString()
    : "0";
}

function readExportActor(row: Row): string {
  const value = firstValue(row, [
    "requestedByName",
    "exportedByName",
    "actorName",
    "userName",
    "requestedBy",
  ]);

  const text = String(value ?? "").trim();

  if (!text) {
    return "System";
  }

  return isUuid(text) ? "System user" : humanize(text);
}

function readExportFile(row: Row): string {
  const value = firstValue(row, [
    "fileName",
    "exportFileName",
    "downloadName",
  ]);

  return String(value ?? "").trim() || "Audit export";
}

function buildDetailItems(row: Row): readonly DetailItem[] {
  return [
    {
      label: "Occurred At",
      value: readOccurredAt(row),
    },
    {
      label: "Action",
      value: readAction(row),
    },
    {
      label: "Entity",
      value: readEntity(row),
    },
    {
      label: "Actor",
      value: readActor(row),
    },
    {
      label: "Result",
      value: readResult(row).replace(/^[^\s]+\s/, ""),
    },
    {
      label: "Linked Record",
      value: readLinkedRecord(row),
    },
    {
      label: "Correlation",
      value: readCorrelationStatus(row).replace(/^[^\s]+\s/, ""),
    },
    {
      label: "Summary",
      value:
        readText(row, [
          "summary",
          "description",
          "message",
          "details",
        ]) || "Audit event recorded",
    },
  ];
}

function hasLinkedComplaint(row: Row): boolean {
  const entityType = String(
    firstValue(row, [
      "entityType",
      "resourceType",
      "module",
      "sourceModule",
    ]) ?? "",
  ).toLowerCase();

  const linkedId = firstValue(row, [
    "complaintId",
    "entityId",
    "recordId",
  ]);

  return Boolean(linkedId) && entityType.includes("complaint");
}

function firstValue(
  row: Row,
  keys: readonly string[],
): unknown {
  for (const key of keys) {
    const value = row[key];

    if (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
    ) {
      return value;
    }
  }

  return undefined;
}

function readText(
  row: Row,
  keys: readonly string[],
): string {
  return String(firstValue(row, keys) ?? "").trim();
}

function humanize(
  value: unknown,
  fallback = "Not available",
): string {
  const text = String(value ?? "").trim();

  if (!text || isUuid(text)) {
    return fallback;
  }

  return text
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatDateTime(value: unknown): string {
  const text = String(value ?? "").trim();

  if (!text) {
    return "Not recorded";
  }

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) {
    return humanize(text, "Not recorded");
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value.trim(),
  );
}

function downloadDisplayedRows(
  rows: readonly Row[],
  format: AuditExportFormat,
) {
  const exportRows = rows.map((row) => ({
    "Occurred At": readOccurredAt(row),
    Action: readAction(row),
    Entity: readEntity(row),
    Actor: readActor(row),
    Result: readResult(row).replace(/^[^\s]+\s/, ""),
    "Linked Record": readLinkedRecord(row),
    Correlation: readCorrelationStatus(row).replace(
      /^[^\s]+\s/,
      "",
    ),
  }));

  const timestamp = new Date()
    .toISOString()
    .slice(0, 16)
    .replace(/[:T]/g, "-");

  if (format === "JSON") {
    saveBlob(
      JSON.stringify(exportRows, null, 2),
      `audit-events-${timestamp}.json`,
      "application/json;charset=utf-8",
    );
    return;
  }

  if (format === "XLS") {
    const headers = Object.keys(exportRows[0] ?? {});
    const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
        </head>
        <body>
          <table border="1">
            <thead>
              <tr>
                ${headers
                  .map(
                    (header) =>
                      `<th>${escapeHtml(header)}</th>`,
                  )
                  .join("")}
              </tr>
            </thead>
            <tbody>
              ${exportRows
                .map(
                  (row) => `
                    <tr>
                      ${headers
                        .map(
                          (header) =>
                            `<td>${escapeHtml(
                              String(
                                row[
                                  header as keyof typeof row
                                ] ?? "",
                              ),
                            )}</td>`,
                        )
                        .join("")}
                    </tr>
                  `,
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    saveBlob(
      html,
      `audit-events-${timestamp}.xls`,
      "application/vnd.ms-excel;charset=utf-8",
    );
    return;
  }

  const headers = Object.keys(exportRows[0] ?? {});
  const csvRows = [
    headers.map(csvCell).join(","),
    ...exportRows.map((row) =>
      headers
        .map((header) =>
          csvCell(
            row[header as keyof typeof row] ?? "",
          ),
        )
        .join(","),
    ),
  ];

  saveBlob(
    `\uFEFF${csvRows.join("\n")}`,
    `audit-events-${timestamp}.csv`,
    "text/csv;charset=utf-8",
  );
}

function csvCell(value: unknown): string {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function saveBlob(
  content: string,
  fileName: string,
  mimeType: string,
) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(url);
}